// src/features/task/TaskCustomFieldDefsAdmin.tsx
// Admin: custom field definitions for the tenant. Fields are defined once here,
// then referenced by task types and set on individual tasks.
// Wraps List/Create/Update/DeleteCustomFieldDefinition.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useTaskCustomField, useI18n } from '@react/hooks';
import { Button, Card, CardContent, CardHeader, CardTitle, CardDescription, Input, Badge, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useScope } from '@/hooks/useScope';
import type { CustomFieldDefinition, CustomFieldOption } from '@core/types/task';
import { formatEnumLabel } from '@/features/task/logic';
import { CUSTOM_FIELD_TYPES, digitsOnly, diffFields, makeEntity } from '@/features/task/panelHelpers';

const EDITABLE_FIELDS: (keyof CustomFieldDefinition)[] = [
  'display_name', 'is_required', 'options', 'position', 'placeholder', 'group', 'default_value',
];

interface EditFormState {
  display_name: string;
  is_required: boolean;
  position: string;
  placeholder: string;
  group: string;
  default_value: string;
  options: CustomFieldOption[];
}

function slugifyOption(label: string): string {
  return label.toLowerCase().trim().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '') || 'option';
}

export const TaskCustomFieldDefsAdmin = observer(() => {
  const store = useTaskCustomField();
  const { t } = useI18n();
  const scope = useScope();

  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [fieldType, setFieldType] = useState<string>(CUSTOM_FIELD_TYPES[0]);
  const [required, setRequired] = useState(false);
  const [optionsCsv, setOptionsCsv] = useState('');

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditFormState | null>(null);
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [confirmId, setConfirmId] = useState<string | null>(null);

  useEffect(() => { store.ListCustomFieldDefinitions(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const isSelect = fieldType === 'CUSTOM_FIELD_TYPE_SELECT' || fieldType === 'CUSTOM_FIELD_TYPE_MULTI_SELECT';

  const create = async () => {
    if (!slug.trim() || !name.trim()) return;
    const options = isSelect
      ? optionsCsv.split(',').map((s) => s.trim()).filter(Boolean).map((label, i) => ({ slug: slugifyOption(label), label, position: i }))
      : [];
    try {
      await store.CreateCustomFieldDefinition({
        entity: makeEntity('', 'custom_field_definition', scope),
        slug: slug.trim(),
        display_name: name.trim(),
        field_type: fieldType as any,
        is_required: required,
        options,
        position: store.items.length,
      } as any);
      setSlug(''); setName(''); setRequired(false); setOptionsCsv(''); setFieldType(CUSTOM_FIELD_TYPES[0]);
    } catch { /* store.error */ }
  };

  const startEdit = (def: CustomFieldDefinition) => {
    setEditingId(def.entity?.id || null);
    setEditForm({
      display_name: def.display_name,
      is_required: def.is_required,
      position: String(def.position ?? 0),
      placeholder: def.placeholder || '',
      group: def.group || '',
      default_value: def.default_value || '',
      options: def.options ? def.options.map((o) => ({ ...o })) : [],
    });
    setNewOptionLabel('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
    setNewOptionLabel('');
  };

  const addOption = () => {
    if (!editForm || !newOptionLabel.trim()) return;
    const label = newOptionLabel.trim();
    setEditForm({
      ...editForm,
      options: [...editForm.options, { slug: slugifyOption(label), label, position: editForm.options.length }],
    });
    setNewOptionLabel('');
  };

  const removeOption = (idx: number) => {
    if (!editForm) return;
    setEditForm({ ...editForm, options: editForm.options.filter((_, i) => i !== idx) });
  };

  const renameOption = (idx: number, label: string) => {
    if (!editForm) return;
    const options = editForm.options.map((o, i) => (i === idx ? { ...o, label } : o));
    setEditForm({ ...editForm, options });
  };

  const saveEdit = async (def: CustomFieldDefinition) => {
    if (!editForm) return;
    const id = def.entity?.id || '';
    const updated: CustomFieldDefinition = {
      ...def,
      display_name: editForm.display_name.trim(),
      is_required: editForm.is_required,
      position: parseInt(editForm.position || '0', 10),
      placeholder: editForm.placeholder.trim() || undefined,
      group: editForm.group.trim() || undefined,
      default_value: editForm.default_value.trim() || undefined,
      options: editForm.options,
    };
    const paths = diffFields(def, updated, EDITABLE_FIELDS);
    if (paths.length === 0) { cancelEdit(); return; }
    try {
      await store.UpdateCustomFieldDefinition(id, { definition: updated, field_mask: paths } as any);
      cancelEdit();
    } catch { /* store.error */ }
  };

  const remove = async (id: string) => {
    try { await store.DeleteCustomFieldDefinition(id); setConfirmId(null); } catch { /* store.error */ }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('task.cfAdmin.title')}</CardTitle>
        <CardDescription>{t('task.cfAdmin.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

        {store.isLoading ? (
          <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />)}</div>
        ) : store.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('task.cfAdmin.empty')}</p>
        ) : (
          <div className="space-y-2">
            {store.items.map((def) => {
              const id = def.entity?.id || '';
              const isEditing = editingId === id;
              const defIsSelect = def.field_type === 'CUSTOM_FIELD_TYPE_SELECT' || def.field_type === 'CUSTOM_FIELD_TYPE_MULTI_SELECT';
              return (
                <div key={id} className="rounded-md border border-border">
                  <div className="flex items-center justify-between gap-2 px-3 py-2">
                    <div className="min-w-0 flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{def.display_name}</span>
                      <span className="text-xs text-muted-foreground">{def.slug}</span>
                      {def.field_type && <Badge variant="secondary" size="sm">{formatEnumLabel(String(def.field_type), 'CUSTOM_FIELD_TYPE_')}</Badge>}
                      {def.is_required && <Badge variant="warning" size="sm">{t('task.cfAdmin.required')}</Badge>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {isEditing ? (
                        <>
                          <Button size="sm" isLoading={store.isSaving} onClick={() => saveEdit(def)}>{t('common.save')}</Button>
                          <Button size="sm" variant="ghost" onClick={cancelEdit}>{t('common.cancel')}</Button>
                        </>
                      ) : confirmId === id ? (
                        <>
                          <Button size="sm" variant="danger" isLoading={store.isDeleting} onClick={() => remove(id)}>{t('common.confirm')}</Button>
                          <Button size="sm" variant="ghost" onClick={() => setConfirmId(null)}>{t('common.cancel')}</Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => startEdit(def)}>{t('common.edit')}</Button>
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setConfirmId(id)}>{t('common.delete')}</Button>
                        </>
                      )}
                    </div>
                  </div>

                  {isEditing && editForm && (
                    <div className="space-y-3 border-t border-border px-3 py-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Input
                          label={t('task.cfAdmin.displayName')}
                          value={editForm.display_name}
                          onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                        />
                        <Input
                          label={t('task.cfAdmin.position')}
                          value={editForm.position}
                          onChange={(e) => setEditForm({ ...editForm, position: digitsOnly(e.target.value) })}
                          inputMode="numeric"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Input
                          label={t('task.cfAdmin.placeholder')}
                          value={editForm.placeholder}
                          onChange={(e) => setEditForm({ ...editForm, placeholder: e.target.value })}
                        />
                        <Input
                          label={t('task.cfAdmin.group')}
                          value={editForm.group}
                          onChange={(e) => setEditForm({ ...editForm, group: e.target.value })}
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                        <Input
                          label={t('task.cfAdmin.defaultValue')}
                          value={editForm.default_value}
                          onChange={(e) => setEditForm({ ...editForm, default_value: e.target.value })}
                        />
                        <ToggleSwitch
                          label={t('task.cfAdmin.required')}
                          checked={editForm.is_required}
                          onChange={(v) => setEditForm({ ...editForm, is_required: v })}
                        />
                      </div>

                      {defIsSelect && (
                        <div className="space-y-2 pt-2 border-t border-border">
                          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('task.cfAdmin.options')}</span>
                          {editForm.options.length === 0 ? (
                            <p className="text-sm text-muted-foreground">{t('task.cfAdmin.noOptions')}</p>
                          ) : (
                            <div className="space-y-1.5">
                              {editForm.options.map((opt, idx) => (
                                <div key={opt.slug + idx} className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    aria-label={t('task.cfAdmin.optionLabel')}
                                    value={opt.label}
                                    onChange={(e) => renameOption(idx, e.target.value)}
                                    className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
                                  />
                                  <span className="text-xs text-muted-foreground">{opt.slug}</span>
                                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeOption(idx)}>{t('common.remove')}</Button>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              aria-label={t('task.cfAdmin.newOption')}
                              value={newOptionLabel}
                              onChange={(e) => setNewOptionLabel(e.target.value)}
                              placeholder={t('task.cfAdmin.newOptionPlaceholder')}
                              className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground"
                            />
                            <Button size="sm" variant="secondary" onClick={addOption} disabled={!newOptionLabel.trim()}>{t('task.cfAdmin.addOption')}</Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="pt-4 border-t border-border space-y-3">
          <h4 className="text-sm font-medium text-foreground">{t('task.cfAdmin.addNew')}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label={t('task.cfAdmin.displayName')} value={name} onChange={(e) => setName(e.target.value)} placeholder="Story Points" />
            <Input label={t('task.cfAdmin.slug')} value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))} placeholder="story_points" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.cfAdmin.fieldType')}</label>
              <Select value={fieldType} onChange={(e) => setFieldType(e.target.value)}>
                {CUSTOM_FIELD_TYPES.map((ft) => <option key={ft} value={ft}>{formatEnumLabel(ft, 'CUSTOM_FIELD_TYPE_')}</option>)}
              </Select>
            </div>
            <ToggleSwitch label={t('task.cfAdmin.required')} checked={required} onChange={setRequired} className="mt-6" />
          </div>
          {isSelect && (
            <Input label={t('task.cfAdmin.options')} value={optionsCsv} onChange={(e) => setOptionsCsv(e.target.value)} placeholder={t('task.cfAdmin.optionsPlaceholder')} />
          )}
          <div className="flex justify-end">
            <Button onClick={create} disabled={!slug.trim() || !name.trim()} isLoading={store.isSaving}>{t('task.cfAdmin.add')}</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

export default TaskCustomFieldDefsAdmin;
